import React from "react";
import "./Form.scss";
import { useAnalytics } from "../../customHooks/reactGA";
import formatPhoneNumber from "../../utils/formatPhoneNumber";
import { primaryCtaSx } from "../../styles/ctaStyles";
import emailjs from "@emailjs/browser";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

const PHONE_HREF = "tel:+13178121388";

const Form = (props) => {
  const { trackEvent } = useAnalytics();

  const [customerName, setCustomerName] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [submittedPhone, setSubmittedPhone] = React.useState("");
  const [submittedFirstName, setSubmittedFirstName] = React.useState("");

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    severity: "success",
    message: "",
  });

  const closeSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const clearForm = () => {
    setCustomerName("");
    setAddress("");
    setPhone("");
  };

  const splitName = (fullName) => {
    const parts = fullName.trim().split(/\s+/);
    return {
      first_name: parts[0] || "",
      last_name: parts.slice(1).join(" ") || "",
    };
  };

  const getTrackingParams = () => ({
    utm_source: sessionStorage.getItem("utm_source") || "",
    utm_medium: sessionStorage.getItem("utm_medium") || "",
    utm_campaign: sessionStorage.getItem("utm_campaign") || "",
    gclid: sessionStorage.getItem("gclid") || "",
    landing_page:
      sessionStorage.getItem("landing_page") || window.location.href,
    referrer: sessionStorage.getItem("referrer") || document.referrer || "",
  });

  const postToGHL = async () => {
    const { first_name, last_name } = splitName(customerName);
    const trackingParams = getTrackingParams();
    const phoneDigits = phone.replace(/\D/g, "");

    const payload = {
      first_name,
      last_name,
      phone: phoneDigits,
      property_address: address,
      ...trackingParams,
    };

    const response = await fetch(process.env.REACT_APP_GHL_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`GHL webhook failed with status ${response.status}`);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!customerName) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Please enter your name.",
      });
      return;
    }
    if (!address) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Please enter the property address.",
      });
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Please enter a valid phone number.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Keep existing notifications intact.
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          customer_name: customerName,
          property_address: address,
          phone: phoneDigits,
          source_page: window.location.pathname,
          submitted_at: new Date().toISOString(),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );

      // Send lead to GHL as an additional step.
      try {
        await postToGHL();
      } catch (ghlErr) {
        console.error("GHL webhook error:", ghlErr);
      }

      trackEvent("Lead Form", "Submit", "Step 1 Lead");
      window.gtag?.("event", "conversion", {
        send_to: "AW-17688518709/LeadForm",
      });
      window.gtag?.("event", "Thank_You");
      window.fbq?.("track", "Lead", {
        content_name: "Cash Offer Request",
      });

      setSubmittedFirstName(splitName(customerName).first_name || "there");
      setSubmittedPhone(formatPhoneNumber(phone));
      setIsSuccess(true);
      clearForm();
    } catch (err) {
      console.log(err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Something went wrong. Please call us at 317-812-1388",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelColor =
    props.theme.palette.mode === "dark"
      ? props.theme.palette.common.white
      : props.theme.palette.primary.main;

  return (
    <div id="lead-form">
      {isSuccess ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography fontSize="1.25rem" fontWeight={600} color="primary">
            Thanks, {submittedFirstName}. We got your info and will call you at{" "}
            {submittedPhone}.
          </Typography>
          <Typography mt={1}>
            Need us sooner?{" "}
            <a
              href={PHONE_HREF}
              onClick={() =>
                window.gtag?.("event", "call_click", {
                  event_category: "Contact",
                  event_label: "Form Success Call Link",
                })
              }
              style={{
                color: "#c76119",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              Call 317-812-1388
            </a>
          </Typography>
        </Box>
      ) : (
        <Box
          component="form"
          noValidate
          autoComplete="off"
          method="post"
          onSubmit={sendEmail}
        >
          <Box display="flex" flexDirection="column" alignContent="flex-start">
            <Box className="form-row" display="flex">
              <Box padding={{ xs: 0.5, md: 1 }} flex={1}>
                <label>
                  <Typography
                    mb="0.25em"
                    color={labelColor}
                    fontSize={{ xs: "0.875rem", md: "1rem" }}
                  >
                    Name
                  </Typography>
                </label>
                <TextField
                  fullWidth
                  size="small"
                  id="customer_name"
                  name="customer_name"
                  label="First and last name"
                  variant="outlined"
                  autoComplete="name"
                  required
                  inputProps={{ style: { fontSize: "16px" } }}
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                  onFocus={() => trackEvent("Form", "focus", "name")}
                />
              </Box>
            </Box>

            <Box className="form-row" display="flex">
              <Box padding={{ xs: 0.5, md: 1 }} flex={1}>
                <label>
                  <Typography
                    mb="0.25em"
                    color={labelColor}
                    fontSize={{ xs: "0.875rem", md: "1rem" }}
                  >
                    Property Address
                  </Typography>
                </label>
                <TextField
                  fullWidth
                  size="small"
                  id="property_address"
                  name="property_address"
                  label="e.g., 123 Main St, Springfield, IN 46140"
                  variant="outlined"
                  autoComplete="street-address"
                  required
                  inputProps={{ style: { fontSize: "16px" } }}
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  onFocus={() => trackEvent("Form", "focus", "address")}
                />
              </Box>
            </Box>

            <Box className="form-row" display="flex">
              <Box padding={{ xs: 0.5, md: 1 }} flex={1}>
                <label>
                  <Typography
                    mb="0.25em"
                    color={labelColor}
                    fontSize={{ xs: "0.875rem", md: "1rem" }}
                  >
                    Phone Number
                  </Typography>
                </label>
                <TextField
                  fullWidth
                  size="small"
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  inputMode="tel"
                  maxLength={14}
                  inputProps={{ style: { fontSize: "16px" } }}
                  label="(000) 000-0000"
                  variant="outlined"
                  value={phone}
                  onChange={(event) =>
                    setPhone(formatPhoneNumber(event.target.value))
                  }
                  onFocus={() => trackEvent("Form", "focus", "phone")}
                />
              </Box>
            </Box>
          </Box>

          <Box className="sbm-container" ml={{ xs: 0.5, md: 1 }} mt={1}>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
              sx={{
                ...primaryCtaSx,
                width: { xs: "100%", md: "auto" },
              }}
            >
              {isSubmitting ? "Sending..." : "Get My Free Cash Offer"}
            </Button>
            <Typography
              variant="body2"
              color={
                props.theme.palette.mode === "dark" ? "#cfcfcf" : "#6b6b6b"
              }
              sx={{ mt: 1, fontSize: "0.875rem" }}
            >
              No obligation. We'll call within 24 hours, or call{" "}
              <a
                href={PHONE_HREF}
                onClick={() =>
                  window.gtag?.("event", "call_click", {
                    event_category: "Contact",
                    event_label: "Form Under-Button Call Link",
                  })
                }
                style={{ color: "inherit", fontWeight: 600 }}
              >
                317-812-1388
              </a>{" "}
              now.
            </Typography>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {snackbar.severity === "error" && (
        <Box sx={{ position: "fixed", bottom: 16, left: 16, zIndex: 9999 }}>
          <Alert severity="error">
            {snackbar.message}
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              Need us sooner?{" "}
              <a
                href={PHONE_HREF}
                style={{
                  color: "#c76119",
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                Call 317-812-1388
              </a>{" "}
              now.
            </Typography>
          </Alert>
        </Box>
      )}
    </div>
  );
};

export default Form;